<template>
    <div>
        <div v-if="!hasItems(log.lines)" class="fst-italic small text-body-tertiary">
            {{ $t('maintenanceLog.noData') }}
        </div>
        <div v-else>
            <div v-for="(project, projectId) in log.lines" :key="projectId" class="mb-4">
                <h5 class="text-center fw-bold my-3">{{ project.name }}</h5>

                <div v-for="(profile, profileId) in project.profiles" :key="profileId"
                    class="card shadow-sm border-0 mb-3">
                    <div class="card-header fw-bold">{{ profile.name }}</div>
                    <div class="table-responsive">
                        <table class="table table-sm small align-top mb-0">
                            <thead>
                                <tr>
                                    <th style="width: 10%">{{ $t('maintenanceLog.completed') }}</th>
                                    <th style="width: 10%">{{ $t('maintenanceLog.technician') }}</th>
                                    <th style="width: 20%">{{ $t('maintenanceLog.system') }}</th>
                                    <th style="width: 20%">{{ $t('maintenanceLog.workDescription') }}</th>
                                    <th style="width: 40%">{{ $t('maintenanceLog.comment') }}</th>
                                </tr>
                            </thead>
                            <tbody v-for="(task, taskId) in profile.tasks" :key="taskId">
                                <tr>
                                    <td>{{ task.finished_on }}</td>
                                    <td>{{ task.user_name }}</td>
                                    <td>{{ clean(task.object_name) }}</td>
                                    <td>
                                        <router-link v-if="task.module === 'tasks' && task.db_id"
                                            :to="`/tasks/${task.db_id}`">{{ task.name }}</router-link>
                                        <span v-else>{{ task.name }}</span>
                                    </td>
                                    <td><span v-html="task.notes"></span></td>
                                </tr>

                                <!-- Subtasks and task files -->
                                <tr v-if="hasItems(task.subtasks) || hasItems(task.files)">
                                    <td colspan="5" class="pt-0">
                                        <table v-if="hasItems(task.subtasks)" class="table table-sm small mb-2">
                                            <thead>
                                                <tr>
                                                    <th style="width: 50%">{{ $t('maintenanceLog.work') }}</th>
                                                    <th class="text-center" style="width: 5%">{{ $t('maintenanceLog.ok') }}</th>
                                                    <th class="text-center" style="width: 5%">{{ $t('maintenanceLog.attention') }}</th>
                                                    <th style="width: 40%">{{ $t('maintenanceLog.notes') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <template v-for="(sub, subId) in task.subtasks" :key="subId">
                                                    <!-- Title row with nested subtasks -->
                                                    <template v-if="sub.istitle && hasItems(sub.subtasks)">
                                                        <tr class="table-light fw-bold">
                                                            <td>{{ sub.name }}</td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                        </tr>
                                                        <template v-for="(s, sId) in sub.subtasks" :key="sId">
                                                            <tr>
                                                                <td>{{ s.name }}</td>
                                                                <td class="text-center"><span v-if="s.subtask_ok">&#10004;</span></td>
                                                                <td class="text-center"><span v-if="s.needs_attention" v-html="s.needs_attention_status_symbol"></span></td>
                                                                <td><span v-html="s.notes"></span></td>
                                                            </tr>
                                                            <tr v-if="hasItems(s.files)">
                                                                <td></td>
                                                                <td colspan="3">
                                                                    {{ $t('maintenanceLog.files') }}:
                                                                    <a v-for="file in s.files" :key="file.list_id"
                                                                        :href="file.url" target="_blank" class="me-2">{{ file.filename || 'fail' }}</a>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                    </template>
                                                    <!-- Regular subtask row -->
                                                    <template v-else>
                                                        <tr>
                                                            <td>{{ sub.name }}</td>
                                                            <td class="text-center"><span v-if="sub.subtask_ok">&#10004;</span></td>
                                                            <td class="text-center"><span v-if="sub.needs_attention" v-html="sub.needs_attention_status_symbol"></span></td>
                                                            <td><span v-html="sub.notes"></span></td>
                                                        </tr>
                                                    </template>
                                                    <tr v-if="hasItems(sub.files)">
                                                        <td></td>
                                                        <td colspan="3">
                                                            {{ $t('maintenanceLog.files') }}:
                                                            <a v-for="file in sub.files" :key="file.list_id"
                                                                :href="file.url" target="_blank" class="me-2">{{ file.filename || 'fail' }}</a>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>

                                        <table v-if="hasItems(task.files)" class="table table-sm small mb-0">
                                            <thead>
                                                <tr>
                                                    <th>{{ $t('maintenanceLog.files') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="file in task.files" :key="file.list_id">
                                                    <td>
                                                        <a :href="file.url" target="_blank">{{ file.filename || 'fail' }}</a>
                                                        <span v-if="file.notes"> {{ file.notes }}</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MaintenanceLog',
    props: {
        // The report payload from GET /api/{entity}/{id}/maintenance_log
        log: {
            type: Object,
            required: true
        }
    },
    methods: {
        // Report collections are PHP associative arrays -> JSON objects (or [] when
        // empty). Handle both shapes.
        hasItems(collection) {
            if (!collection) return false;
            if (Array.isArray(collection)) return collection.length > 0;
            return Object.keys(collection).length > 0;
        },
        // The report uses "&nbsp;" as a placeholder for a missing value.
        clean(value) {
            if (!value || value === '&nbsp;') return '';
            return value;
        }
    }
};
</script>
